import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Tooltip } from '@mui/material';

import LeadsTable from '../../../../components/Leads/components/LeadsTable';
import PageContent from '../../../../components/PageContent';
import DateFilter from '../../../../components/DateFilter';
import { LeadsTableType } from '../../../../components/Leads/components/LeadsTable/utils';
import { AllLeadStates, SalesStage } from '../../../../Model/Lead';
import { DarkTag, TransparentTag } from '../../../../../components/Tags';
import { DateFormat } from '../../../../../utils/date';
import { DateValueText } from '../../styles';
import { getDateStatusColor, sortLeadsByDateField } from '../dateStatusUtils';
import texts from './texts.json';
import { ChipContainer, TabHeaderRow } from './styles';

const hiddenColumns = ['salesStage'];

const FollowUps = ({
  coachesList,
  setSelectedLead,
  setShowQuestionsModal,
  onOnboardingLinkClick,
  onNotesClick,
  leads,
  userId,
  userName,
  selectedPeriod,
  setSelectedPeriod,
}) => {
  const sortedLeads = useMemo(() => (
    sortLeadsByDateField(leads, 'followUpDate')
  ), [leads]);

  const extraColumns = useMemo(
    () => [
      {
        field: 'contractCreatedAt',
        headerName: texts.contract,
        flex: 14,
        valueGetter: ({ row }) => row.contractCreatedAt,
        renderCell: ({ value }) => {
          const formattedContractCreatedAt = value
            ? moment(value.toDate()).format(DateFormat.DATE_MONTH_SHORT_DISPLAY_FORMAT)
            : null;

          const contractCreatedText = formattedContractCreatedAt
            ? texts.contractCreatedAt.replace('{date}', formattedContractCreatedAt)
            : texts.empty;

          return (
            <ChipContainer>
              {formattedContractCreatedAt
                ? <DarkTag>{contractCreatedText}</DarkTag>
                : <TransparentTag>{texts.empty}</TransparentTag>}
            </ChipContainer>
          );
        },
      },
      {
        field: 'followUpDate',
        headerName: texts.followUpDate,
        flex: 15,
        valueGetter: ({ row }) => row.followUpDate,
        renderCell: ({ value }) => {
          const color = getDateStatusColor(value);
          const formattedFollowUpDate = value
            ? moment(value.toDate()).format(DateFormat.DATE_MONTH_YEAR_TIME_SHORT_DISPLAY_FORMAT)
            : texts.empty;

          return (
            <Tooltip title={formattedFollowUpDate} enterDelay={0} arrow>
              <DateValueText $color={color}>
                {`${value ? moment(value.toDate()).fromNow() : texts.empty}`}
              </DateValueText>
            </Tooltip>
          );
        },
      },
    ],
    [],
  );

  return (
    <PageContent>
      <TabHeaderRow>
        <DateFilter
          selectedPeriod={selectedPeriod}
          onChange={setSelectedPeriod}
        />
      </TabHeaderRow>
      <LeadsTable
        rows={sortedLeads}
        selectLead={setSelectedLead}
        setShowQuestionsModal={setShowQuestionsModal}
        onOnboardingLinkClick={onOnboardingLinkClick}
        selectedLeadStates={AllLeadStates}
        selectedSalesStages={[SalesStage.FOLLOW_UP]}
        type={LeadsTableType.IS_LEADS}
        userId={userId}
        userName={userName}
        onNotesClick={onNotesClick}
        coachesList={coachesList}
        activeSalesStageFilter={SalesStage.FOLLOW_UP}
        hiddenColumns={hiddenColumns}
        extraColumns={extraColumns}
        isDisableStatusChange
      />
    </PageContent>
  );
};

FollowUps.propTypes = {
  setSelectedLead: PropTypes.func.isRequired,
  setShowQuestionsModal: PropTypes.func.isRequired,
  onOnboardingLinkClick: PropTypes.func.isRequired,
  onNotesClick: PropTypes.func.isRequired,
  coachesList: PropTypes.object,
  leads: PropTypes.array.isRequired,
  userId: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  selectedPeriod: PropTypes.number.isRequired,
  setSelectedPeriod: PropTypes.func.isRequired,
};

FollowUps.defaultProps = {
  coachesList: {},
};

export default FollowUps;
